import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Textarea } from '../components/Textarea';
import { Input } from '../components/Input';

export const SubmitFeedbackPage = () => {
  const [overallFeedback, setOverallFeedback] = useState('');
  const [plotFeedback, setPlotFeedback] = useState('');
  const [characterFeedback, setCharacterFeedback] = useState('');
  const [pacingFeedback, setPacingFeedback] = useState('');
  const [writingStyleFeedback, setWritingStyleFeedback] = useState('');
  const [worldbuildingFeedback, setWorldbuildingFeedback] = useState('');
  const [overallRating, setOverallRating] = useState(0);
  const [wouldRecommend, setWouldRecommend] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Extract manuscript ID from URL - in real app would use router
  const manuscriptId = window.location.pathname.match(/\/reader\/manuscripts\/(\d+)\/feedback/)?.[1];

  // Mock data - in real app, would fetch from API
  const manuscript = {
    id: manuscriptId,
    title: 'Echoes of the Archive',
    author: 'Eleanor Hart',
    genre: 'Historical Fiction',
    wordCount: 95000,
    readingProgress: 100,
  };

  const handleSubmit = () => {
    if (!overallFeedback.trim()) {
      window.alert('Please provide overall feedback before submitting.');
      return;
    }

    if (overallRating === 0) {
      window.alert('Please provide a rating before submitting.');
      return;
    }

    const feedbackData = {
      manuscriptId,
      overallFeedback,
      categoryFeedback: {
        plot: plotFeedback,
        characters: characterFeedback,
        pacing: pacingFeedback,
        writingStyle: writingStyleFeedback,
        worldbuilding: worldbuildingFeedback,
      },
      rating: overallRating,
      wouldRecommend,
      submittedDate: new Date().toISOString(),
    };

    console.log('Submitting feedback:', feedbackData);
    window.alert('Thank you! Your feedback has been submitted to the author.');
    window.location.href = '/reader/manuscripts';
  };

  const renderStarRating = () => {
    return (
      <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
      }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => setOverallRating(star)}
            style={{
              fontSize: '32px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              transition: 'transform 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            {star <= overallRating ? '⭐' : '☆'}
          </button>
        ))}
        <span style={{
          marginLeft: '8px',
          fontSize: '16px',
          color: theme.colors.textSecondary,
        }}>
          {overallRating > 0 ? `${overallRating}/5` : 'Select rating'}
        </span>
      </div>
    );
  };

  if (showPreview) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
      }}>
        <Header />
        <main style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '60px 40px',
        }}>
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '8px',
              color: theme.colors.textPrimary,
            }}>
              Preview Your Feedback
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              Review before submitting to {manuscript.author}
            </p>
          </div>

          <Card style={{ marginBottom: '24px' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '500',
              marginBottom: '16px',
              color: theme.colors.textPrimary,
            }}>
              {manuscript.title}
            </h2>

            {/* Overall Rating */}
            <div style={{
              padding: '16px',
              backgroundColor: theme.colors.primaryLight,
              borderRadius: theme.borderRadius.medium,
              marginBottom: '24px',
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: '48px',
                marginBottom: '8px',
              }}>
                {'⭐'.repeat(overallRating)}
              </div>
              <div style={{
                fontSize: '18px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
              }}>
                {overallRating}/5 Stars
              </div>
              {wouldRecommend && (
                <div style={{
                  fontSize: '14px',
                  color: theme.colors.success,
                  marginTop: '8px',
                }}>
                  ✓ Would recommend to other readers
                </div>
              )}
            </div>

            {/* Overall Feedback */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '12px',
                color: theme.colors.textPrimary,
              }}>
                Overall Feedback
              </h3>
              <div style={{
                padding: '16px',
                backgroundColor: theme.colors.backgroundAlt,
                borderRadius: theme.borderRadius.small,
                fontSize: '15px',
                lineHeight: '1.8',
                color: theme.colors.textPrimary,
                whiteSpace: 'pre-wrap',
              }}>
                {overallFeedback}
              </div>
            </div>

            {/* Category Feedback */}
            {(plotFeedback || characterFeedback || pacingFeedback || writingStyleFeedback || worldbuildingFeedback) && (
              <div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  marginBottom: '16px',
                  color: theme.colors.textPrimary,
                }}>
                  Detailed Feedback
                </h3>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}>
                  {plotFeedback && (
                    <div>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        marginBottom: '8px',
                        color: theme.colors.textSecondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        Plot
                      </h4>
                      <div style={{
                        padding: '12px',
                        backgroundColor: theme.colors.backgroundAlt,
                        borderRadius: theme.borderRadius.small,
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: theme.colors.textPrimary,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {plotFeedback}
                      </div>
                    </div>
                  )}

                  {characterFeedback && (
                    <div>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        marginBottom: '8px',
                        color: theme.colors.textSecondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        Characters
                      </h4>
                      <div style={{
                        padding: '12px',
                        backgroundColor: theme.colors.backgroundAlt,
                        borderRadius: theme.borderRadius.small,
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: theme.colors.textPrimary,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {characterFeedback}
                      </div>
                    </div>
                  )}

                  {pacingFeedback && (
                    <div>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        marginBottom: '8px',
                        color: theme.colors.textSecondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        Pacing
                      </h4>
                      <div style={{
                        padding: '12px',
                        backgroundColor: theme.colors.backgroundAlt,
                        borderRadius: theme.borderRadius.small,
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: theme.colors.textPrimary,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {pacingFeedback}
                      </div>
                    </div>
                  )}

                  {writingStyleFeedback && (
                    <div>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        marginBottom: '8px',
                        color: theme.colors.textSecondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        Writing Style
                      </h4>
                      <div style={{
                        padding: '12px',
                        backgroundColor: theme.colors.backgroundAlt,
                        borderRadius: theme.borderRadius.small,
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: theme.colors.textPrimary,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {writingStyleFeedback}
                      </div>
                    </div>
                  )}

                  {worldbuildingFeedback && (
                    <div>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        marginBottom: '8px',
                        color: theme.colors.textSecondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        Worldbuilding
                      </h4>
                      <div style={{
                        padding: '12px',
                        backgroundColor: theme.colors.backgroundAlt,
                        borderRadius: theme.borderRadius.small,
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: theme.colors.textPrimary,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {worldbuildingFeedback}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px',
          }}>
            <Button
              variant="ghost"
              onClick={() => setShowPreview(false)}
            >
              ← Back to Edit
            </Button>
            <Button onClick={handleSubmit}>
              Submit Feedback
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />
      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Submit Feedback
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            {manuscript.title} by {manuscript.author}
          </p>
        </div>

        {/* Progress Check */}
        {manuscript.readingProgress < 100 && (
          <Card style={{
            backgroundColor: theme.colors.warningLight,
            borderLeft: `4px solid ${theme.colors.warning}`,
            marginBottom: '24px',
          }}>
            <p style={{
              fontSize: '15px',
              color: theme.colors.textPrimary,
              margin: 0,
            }}>
              <strong>Note:</strong> You're currently at {manuscript.readingProgress}% progress.
              You can submit interim feedback now or wait until you've finished the entire manuscript.
            </p>
          </Card>
        )}

        {/* Overall Rating */}
        <Card style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Overall Rating *
          </h2>
          {renderStarRating()}
        </Card>

        {/* Overall Feedback */}
        <Card style={{ marginBottom: '24px' }}>
          <Textarea
            label="Overall Feedback *"
            placeholder="Share your overall thoughts about the manuscript. What worked well? What could be improved? What was your reading experience like?"
            value={overallFeedback}
            onChange={(e) => setOverallFeedback(e.target.value)}
            rows={10}
            required
            helperText="This is the most important section - be honest and constructive"
          />
        </Card>

        {/* Category Feedback */}
        <Card style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '500',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Detailed Category Feedback (Optional)
          </h2>
          <p style={{
            fontSize: '14px',
            color: theme.colors.textSecondary,
            marginBottom: '24px',
          }}>
            Provide specific feedback on different aspects of the manuscript
          </p>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}>
            <Textarea
              label="Plot & Structure"
              placeholder="Comments on the plot, story structure, pacing of events, plot holes, etc."
              value={plotFeedback}
              onChange={(e) => setPlotFeedback(e.target.value)}
              rows={5}
            />

            <Textarea
              label="Characters"
              placeholder="Thoughts on character development, believability, relationships, motivations, etc."
              value={characterFeedback}
              onChange={(e) => setCharacterFeedback(e.target.value)}
              rows={5}
            />

            <Textarea
              label="Pacing"
              placeholder="Comments on the pacing - too slow, too fast, well-balanced? Where did it drag or rush?"
              value={pacingFeedback}
              onChange={(e) => setPacingFeedback(e.target.value)}
              rows={5}
            />

            <Textarea
              label="Writing Style & Prose"
              placeholder="Feedback on the writing quality, voice, dialogue, descriptions, grammar, etc."
              value={writingStyleFeedback}
              onChange={(e) => setWritingStyleFeedback(e.target.value)}
              rows={5}
            />

            <Textarea
              label="Worldbuilding / Setting"
              placeholder="For genre fiction: thoughts on the world, setting, magic system, etc."
              value={worldbuildingFeedback}
              onChange={(e) => setWorldbuildingFeedback(e.target.value)}
              rows={5}
            />
          </div>
        </Card>

        {/* Recommendation */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <input
              type="checkbox"
              id="recommend"
              checked={wouldRecommend}
              onChange={(e) => setWouldRecommend(e.target.checked)}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer',
              }}
            />
            <label
              htmlFor="recommend"
              style={{
                fontSize: '16px',
                color: theme.colors.textPrimary,
                cursor: 'pointer',
              }}
            >
              I would recommend this manuscript to other beta readers
            </label>
          </div>
        </Card>

        {/* Feedback Guidelines */}
        <Card style={{
          backgroundColor: theme.colors.backgroundAlt,
          marginBottom: '32px',
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '500',
            marginBottom: '12px',
            color: theme.colors.textPrimary,
          }}>
            Feedback Guidelines
          </h3>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            fontSize: '14px',
            lineHeight: '1.8',
            color: theme.colors.textSecondary,
          }}>
            <li>Be honest but kind - writers are sharing vulnerable work</li>
            <li>Focus on what could be improved, not just what's wrong</li>
            <li>Provide specific examples when possible</li>
            <li>Consider the author's goals and genre conventions</li>
            <li>Remember: critique the work, not the writer</li>
            <li>Highlight what worked well, not just problems</li>
          </ul>
        </Card>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
        }}>
          <Button
            variant="ghost"
            onClick={() => window.location.href = `/reader/manuscripts/${manuscriptId}/read`}
          >
            ← Cancel
          </Button>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              variant="outline"
              onClick={() => setShowPreview(true)}
              disabled={!overallFeedback.trim() || overallRating === 0}
            >
              Preview
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!overallFeedback.trim() || overallRating === 0}
            >
              Submit Feedback
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
